import { cloneDeep, isEqual } from 'lodash';

import { formatStoreAlias, ISOtoHMS } from '../../utils/formatting';
import bopis from '../o2o-capabilities/bopis/constants';
import {
  carrierPickupTimes as defaultCarrierPickupTimes,
  daysOfTheWeek,
  defaultCapacity as defaultCapacityConst,
  sfs,
} from '../o2o-capabilities/sfs/constants';

export const DEFAULT_LEAD_TIME_DURATION = 'PT2H';

export const DEFAULT_CHUNK_SIZE = 6;

// Pure number-input clamp helper. Returns the parsed integer bounded to
// [min, max], or null when the raw input is empty / non-numeric. Replaces the
// previous pattern that mutated `event.target.value` directly (and required
// `// eslint-disable no-param-reassign` to satisfy the linter). Callers
// decide how to render a null result (empty string vs. fallback default).
export const clampInt = (rawValue, min, max) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = parseInt(rawValue, 10);
  if (Number.isNaN(parsed)) return null;
  return Math.min(Math.max(parsed, min), max);
};

export const chunkArray = (arr, size = DEFAULT_CHUNK_SIZE) => Array.from(
  { length: Math.ceil((arr || []).length / size) },
  (_, i) => arr.slice(i * size, i * size + size),
);

export const dateRangeOverlaps = (aStart, aEnd, bStart, bEnd) => (
  (aStart <= bStart && bStart <= aEnd)
  || (aStart <= bEnd && bEnd <= aEnd)
  || (bStart < aStart && aEnd < bEnd)
);

export const multipleDateRangeOverlaps = (ranges) => {
  const dateRanges = (ranges || []).filter((range) => range && range.from && range.to);
  for (let i = 0; i < dateRanges.length - 1; i += 1) {
    for (let j = i + 1; j < dateRanges.length; j += 1) {
      if (dateRangeOverlaps(
        dateRanges[i].from,
        dateRanges[i].to,
        dateRanges[j].from,
        dateRanges[j].to,
      )) return true;
    }
  }
  return false;
};

export const findIndexOfService = (services, storeServiceTypeId) => {
  const index = (services || []).findIndex((service) => service.storeServiceTypeId === storeServiceTypeId);
  return index >= 0 ? index : (services || []).length;
};

export const buildBopisPutBody = (currentStore, { enabled, leadTimeISO }) => {
  const next = cloneDeep(currentStore);
  next.storeServices = next.storeServices || [];
  const index = findIndexOfService(next.storeServices, bopis.storeServiceTypeId);
  const existing = next.storeServices[index];
  if (existing) {
    existing.enabled = enabled;
    existing.fulfillmentGuarantees = existing.fulfillmentGuarantees || {};
    existing.fulfillmentGuarantees.leadTime = leadTimeISO;
  } else {
    next.storeServices[index] = {
      ...bopis,
      enabled,
      fulfillmentGuarantees: { leadTime: leadTimeISO },
    };
  }
  return { new: next, old: currentStore };
};

export const buildSfsStoreServicesPutBody = (currentStore, { enabled }) => {
  const next = cloneDeep(currentStore);
  next.storeServices = next.storeServices || [];
  const index = findIndexOfService(next.storeServices, sfs.storeServiceTypeId);
  const existing = next.storeServices[index];
  if (existing) {
    existing.enabled = enabled;
  } else {
    next.storeServices[index] = { ...sfs, enabled };
  }
  return { new: next, old: currentStore };
};

// The Fulfillment Capacities schema requires `carrierPickupTimes` and
// `defaultLeadTimeDuration` even when creating a new record. For PUT we
// preserve whatever the existing record has so we don't accidentally
// wipe fields the bulk page doesn't expose; for POST we fall back to
// the same defaults the single-store page uses.
// `capacityExceptions` here is the COMPLETE list for the store (REPLACE
// semantics) - per-row editing means the user has full control.
export const buildSfsCapacitiesBody = ({ existing, defaultCapacity, capacityExceptions }) => {
  const existingPayload = existing ? cloneDeep(existing) : {};
  return {
    carrierPickupTimes: defaultCarrierPickupTimes,
    defaultLeadTimeDuration: DEFAULT_LEAD_TIME_DURATION,
    ...existingPayload,
    capacityExceptions: capacityExceptions || [],
    defaultCapacity,
  };
};

// Per-row form derivation: given an entry in currentValuesCache, return the
// canonical "starting" form values for that store. Used to seed
// formValuesByStore and to detect dirty rows.
export const deriveBopisFormValues = (cached) => {
  const bopisRow = (cached && cached.bopis) || {};
  return {
    enabled: !!bopisRow.enabled,
    leadTimeData: ISOtoHMS(bopisRow.leadTimeISO || null),
  };
};

export const deriveSfsFormValues = (cached) => {
  const sfsRecord = (cached && cached.sfs) || null;
  const baseCapacity = (sfsRecord && sfsRecord.defaultCapacity) || defaultCapacityConst;
  // Build a fresh defaultCapacity with all days populated (in case the existing
  // record is missing some) so the editable inputs always have a value.
  const defaultCapacity = daysOfTheWeek.reduce((acc, day) => ({
    ...acc,
    [day]: { maxUnits: (baseCapacity[day] && Number.isFinite(baseCapacity[day].maxUnits)) ? baseCapacity[day].maxUnits : 0 },
  }), {});
  return {
    capacityExceptions: cloneDeep((sfsRecord && sfsRecord.capacityExceptions) || []),
    defaultCapacity,
    enabled: !!(cached && cached.sfsEnabled),
  };
};

export const isBopisRowDirty = (current, cached) => !isEqual(current, deriveBopisFormValues(cached));

export const isSfsRowDirty = (current, cached) => !isEqual(current, deriveSfsFormValues(cached));

export const isBopisRowValid = (current) => {
  if (!current || !current.leadTimeData) return false;
  const { hours, minutes, seconds } = current.leadTimeData;
  return !!(hours && minutes && seconds);
};

export const isSfsRowValid = (current) => {
  if (!current) return false;
  const exceptions = current.capacityExceptions || [];
  const allExceptionsValid = exceptions.every((e) => (
    e && e.from && e.to && e.from <= e.to
    && Number.isInteger(e.maxUnits) && e.maxUnits >= 0
  ));
  if (!allExceptionsValid) return false;
  if (multipleDateRangeOverlaps(exceptions)) return false;
  return true;
};

const csvEscape = (value) => {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const downloadFailuresCsv = (failures) => {
  const header = 'storeAlias,errorMessage';
  const rows = (failures || []).map((failure) => [
    csvEscape(failure.storeAlias),
    csvEscape(failure.message),
  ].join(','));
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'O2O_Bulk_Update_Failures.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const buildFailureRecord = (store, message) => ({
  country: store.address && store.address.country,
  message,
  storeAlias: formatStoreAlias(store),
  storeId: store.id,
  storeNumber: store.storeNumber,
});

// Returns true if any comma-separated term in `search` is a substring of the
// store's storeNumber, name, or facilityType. Empty search matches every
// store. Whitespace around commas is ignored so "1234, 5678 , 9012" works.
export const storeMatchesSearch = (store, search) => {
  if (!search) return true;
  const terms = String(search)
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (terms.length === 0) return true;
  const number = (store.storeNumber || '').toLowerCase();
  const name = (store.name || '').toLowerCase();
  const facility = (store.facilityType || '').toLowerCase();
  return terms.some((q) => number.includes(q) || name.includes(q) || facility.includes(q));
};
